import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  ChevronDown,
  Monitor,
  Moon,
  Sparkles,
  Sun,
  Target,
} from "lucide-react";
import { cn } from "@/cn";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useApi";
import {
  applyTheme,
  cacheThemePreference,
  readCachedThemePreference,
  subscribeToSystemTheme,
  themePreferenceLabel,
  type ThemePreference,
} from "@/lib/theme";

function initialsFrom(name?: string | null, email?: string | null): string {
  const src = (name ?? email ?? "?").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
  }
  return src.slice(0, 2).toUpperCase() || "?";
}

export function NotificationsBell({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={cn("relative shrink-0", className)} ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground"
        title="Notifications"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell className="size-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-md border border-border bg-card shadow-lg"
        >
          <div className="border-b border-border px-3 py-2.5">
            <p className="text-sm font-medium text-foreground">Notifications</p>
          </div>
          <p className="px-3 py-10 text-center text-sm text-muted-foreground">
            You&apos;re all caught up. New alerts will show up here.
          </p>
        </div>
      )}
    </div>
  );
}

export function UserMenu({ className }: { className?: string }) {
  const { user, signOut } = useAuth();
  const { data: profile } = useProfile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState<ThemePreference>("system");
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName =
    profile?.name?.trim() ||
    user?.name?.trim() ||
    user?.email?.trim() ||
    "Account";

  useEffect(() => {
    setTheme(readCachedThemePreference());
  }, []);

  useEffect(() => {
    if (theme !== "system") return;
    return subscribeToSystemTheme(() => applyTheme("system"));
  }, [theme]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  function setThemePreference(next: ThemePreference) {
    setTheme(next);
    cacheThemePreference(next);
    applyTheme(next);
  }

  return (
    <div className={cn("relative shrink-0", className)} ref={menuRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-sm text-foreground hover:bg-surface-hover"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        title={displayName}
      >
        <span className="flex size-8 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand">
          {initialsFrom(displayName, user?.email)}
        </span>
        <ChevronDown className="hidden size-3.5 text-muted-foreground sm:block" />
      </button>
      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-56 overflow-hidden rounded-md border border-border bg-card py-1 shadow-lg"
        >
          <div className="border-b border-border px-3 py-2">
            <p className="truncate text-sm font-medium text-foreground">
              {displayName}
            </p>
            {user?.email ? (
              <p className="truncate text-xs text-muted-foreground">
                {user.email}
              </p>
            ) : null}
          </div>
          <MenuLink to="/profile" onClick={() => setMenuOpen(false)}>
            Profile
          </MenuLink>
          <MenuLink
            to="/profile/ai-context"
            onClick={() => setMenuOpen(false)}
          >
            AI Context
          </MenuLink>
          <MenuLink to="/goals" onClick={() => setMenuOpen(false)}>
            Goals
          </MenuLink>
          <MenuLink to="/settings/ai" onClick={() => setMenuOpen(false)}>
            App settings
          </MenuLink>
          <div className="border-t border-border px-3 py-2">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Theme
            </p>
            <div className="flex gap-1">
              {(
                [
                  ["system", "System"] as const,
                  ["light", "Light"] as const,
                  ["dark", "Dark"] as const,
                ]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setThemePreference(value)}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-1 rounded-md px-1.5 py-1.5 text-[11px]",
                    theme === value
                      ? "bg-brand-soft text-brand"
                      : "text-muted-foreground hover:bg-surface-hover",
                  )}
                  title={label}
                >
                  {value === "system" ? (
                    <Monitor className="size-3" />
                  ) : value === "light" ? (
                    <Sun className="size-3" />
                  ) : (
                    <Moon className="size-3" />
                  )}
                </button>
              ))}
            </div>
            <p className="mt-1 text-center text-[10px] text-muted-foreground">
              {themePreferenceLabel(theme)}
            </p>
          </div>
          <button
            type="button"
            role="menuitem"
            className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-surface-hover"
            onClick={() => {
              setMenuOpen(false);
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MenuLink({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      className="block px-3 py-2 text-sm text-foreground hover:bg-surface-hover"
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

/** Desktop sticky top chrome: Goals · AI · Notifications · Avatar */
export function TopAppBar({ className }: { className?: string }) {
  return (
    <header
      className={cn(
        "sticky top-0 z-20 hidden items-center justify-end gap-1 border-b border-border bg-bg/90 px-4 py-2 backdrop-blur md:flex",
        className,
      )}
    >
      <Link
        to="/goals"
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-muted-foreground transition hover:bg-surface-hover hover:text-foreground"
      >
        <Target className="size-3.5" />
        Goals
      </Link>
      <Link
        to="/ai-assistant"
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-muted-foreground transition hover:bg-surface-hover hover:text-foreground"
      >
        <Sparkles className="size-3.5" />
        AI Assistant
      </Link>
      <NotificationsBell />
      <UserMenu />
    </header>
  );
}
